import { api } from '@/api';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { FormMode } from '@/components/forms/types';

import { SortableEvent, SortableOptions } from 'sortablejs';
import { computed, Ref, ref, nextTick } from 'vue';

export function useDeleteJobConfigurationWithConfirmation() {
  return useActionWithConfirmation({
    confirmedAction: async (id: string) =>
      await api.jobConfigurationsApi.deleteJobConfiguration({
        id: id,
      }),
  });
}

export function useDraggableExpandpanels(
  move: (oldIndex: number, newIndex: number) => void,
  mode: Ref<FormMode>,
  items: Ref<any[]>,
) {
  const expandedIndices = ref<number[] | undefined>();
  const isDragging = ref(false);

  const sortableOptions = computed<SortableOptions>(() => ({
    handle: '.handle',
    disabled: mode.value === 'details',
    scroll: true,
    scrollSensitivity: 300,
    forceFallback: true,
  }));

  function adaptExpanded(oldIndex: number, newIndex: number, expandedIndex: number) {
    if (expandedIndex === oldIndex) return newIndex;
    if (expandedIndex > oldIndex && expandedIndex <= newIndex) return expandedIndex - 1;
    if (expandedIndex < oldIndex && expandedIndex >= newIndex) return expandedIndex + 1;
    return expandedIndex;
  }

  function dragStart() {
    isDragging.value = true;
  }

  function drag(event: SortableEvent) {
    const { oldIndex, newIndex } = event;
    if (oldIndex !== undefined && newIndex !== undefined) {
      move(oldIndex, newIndex);
      expandedIndices.value = expandedIndices.value?.map((i) =>
        adaptExpanded(oldIndex, newIndex, i),
      );
    }

    nextTick(() => {
      isDragging.value = false;
    });
  }

  function expandAll() {
    expandedIndices.value = items.value.map((_, index) => index);
  }

  function collapseAll() {
    expandedIndices.value = undefined;
  }

  const anyExpanded = computed<boolean>(() => !!expandedIndices.value?.length);
  const allExpanded = computed<boolean>(() =>
    items.value.every((_, index) => expandedIndices.value?.includes(index)),
  );

  return {
    expandedIndices,
    expandAll,
    collapseAll,
    anyExpanded,
    allExpanded,
    dragStart,
    drag,
    isDragging,
    sortableOptions,
  };
}
